import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Filter, ChevronDown, X } from "@/components/icons";

// NOTI : 완전 단일 값 쓸일 없을듯...?

const filterButtonVariants = cva(
  "relative flex items-center justify-center gap-2.5 overflow-hidden rounded-md px-3 py-2 transition-all",
  {
    variants: {
      status: {
        normal: "bg-static-white inset-border-filter-normal",
        form: "bg-static-white inset-border-solid-normal",
        selected: "bg-primary-strong/2 inset-outline-border-gradient",
      },
      disabled: {
        true: "cursor-not-allowed",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      {
        status: "normal",
        disabled: false,
        className: "active:bg-primary-strong/10",
      },
      {
        status: "selected",
        disabled: false,
        className: "active:bg-primary-strong/10",
      },
    ],
    defaultVariants: {
      status: "normal",
      disabled: false,
    },
  },
);

export interface FilterButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">,
    VariantProps<typeof filterButtonVariants> {
  /** 필터 라벨 */
  label: string;
  /** 선택된 값 (selected 상태일 때 표시) */
  selectedValue?: string;
  /** 필터 아이콘 표시 여부 */
  showFilterIcon?: boolean;
  /** 오른쪽 아이콘 표시 여부 (normal: chevron, selected: X) */
  showRightIcon?: boolean;
  /** 선택 해제 콜백 */
  onClear?: () => void;
  /** 비활성화 여부 */
  disabled?: boolean;
}

const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  (
    {
      className,
      status = "normal",
      label,
      selectedValue,
      showFilterIcon = true,
      showRightIcon = true,
      onClear,
      onClick,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const isSelected = status === "selected" || !!selectedValue;
    const currentStatus = isSelected ? "selected" : "normal";

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
    };

    const handleClearClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onClear?.();
    };

    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={cn(
          filterButtonVariants({
            status: disabled ? "normal" : currentStatus,
            disabled,
          }),
          "group",
          className,
        )}
        onClick={handleClick}
        {...props}
      >
        {/* Contents */}
        <div
          className={cn("flex items-center gap-1", disabled && "opacity-40")}
        >
          {/* Filter Icon */}
          {showFilterIcon && (
            <Filter
              size={16}
              color={
                isSelected && !disabled ? "primary-normal" : "labels-primary"
              }
            />
          )}

          {/* Label */}
          <span
            className={cn(
              "typo-label-1-semibold whitespace-nowrap",
              isSelected && !disabled
                ? "text-primary-normal"
                : "text-labels-alternative",
            )}
          >
            {label}
          </span>

          {/* Selected Value */}
          {isSelected && selectedValue && (
            <span
              className={cn(
                "typo-label-1-semibold whitespace-nowrap",
                disabled ? "text-labels-alternative" : "text-primary-strong",
              )}
            >
              {selectedValue}
            </span>
          )}

          {/* Right Icon */}
          {showRightIcon && (
            <>
              {isSelected ? (
                <span
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={disabled ? undefined : handleClearClick}
                  onKeyDown={
                    disabled
                      ? undefined
                      : (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleClearClick(e as unknown as React.MouseEvent);
                          }
                        }
                  }
                  className={cn(
                    "flex items-center justify-center",
                    disabled ? "cursor-not-allowed" : "cursor-pointer",
                  )}
                  aria-label="필터 해제"
                >
                  <X size={16} color="labels-alternative" />
                </span>
              ) : (
                <ChevronDown size={16} color="labels-alternative" />
              )}
            </>
          )}
        </div>

        {/* Dim overlay for hover/pressed states */}
        {!disabled && (
          <div
            className={cn(
              "absolute -inset-px pointer-events-none transition-opacity rounded-md",
              "bg-static-black",
              "opacity-0 group-hover:opacity-[0.02] group-active:opacity-10",
            )}
          />
        )}
      </button>
    );
  },
);

FilterButton.displayName = "FilterButton";

export { FilterButton, filterButtonVariants };
